// /////////////////////////////////////////////////////////////
// [ファイル] PipelineFunnelBoard.tsx
// [概要] 案件推進ボード（スライド「KPIの設計=売上の見込み管理」の構図）
//        最新断面を 見込み→D→C→B→A→(成約ライン)→S のファネルで表示する。
//        柱の高さ=件数(√スケール)、柱内の金額=額面残計。
//        各柱の下に期間内の失注、前断面があれば増減バッジを表示する。
//        データは既存の summary / lostRows / phases をそのまま使う（追加API不要）。
// /////////////////////////////////////////////////////////////
import React, { useMemo } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import {
  PipelineSummaryRow,
  PipelinePhaseMaster,
  LostAnalysisRow,
} from '../hooks/useSfaPipeline';

// ドリルダウン呼び出し用の柱情報（SfaPipelineDashboard の MatrixRowDef 互換）
export interface FunnelColumnRef {
  key: string;
  label: string;
  phase?: string;
  aggregateClass?: string;
  rankCode?: string;
  isTotal?: boolean;
}

interface Props {
  snapshotDate: string;
  summary: PipelineSummaryRow[];   // 内訳なし集計の全断面分
  lostRows: LostAnalysisRow[];     // フィルター期間内の失注集計
  phases: PipelinePhaseMaster[];
  onColumnClick: (col: FunnelColumnRef) => void;
}

// フェーズ先頭数字 → ファネル配色（既存フェーズチップの温度勾配と同一トーン）
const FUNNEL_COLORS: Record<string, { bg: string; border: string; fg: string }> = {
  '1': { bg: '#eceff1', border: '#b0bec5', fg: '#455a64' }, // 見込み blueGrey
  '2': { bg: '#e3f2fd', border: '#64b5f6', fg: '#1565c0' }, // D blue
  '3': { bg: '#e0f7fa', border: '#4dd0e1', fg: '#00838f' }, // C cyan
  '4': { bg: '#fff8e1', border: '#ffca28', fg: '#f57f17' }, // B amber
  '5': { bg: '#fff3e0', border: '#ffb74d', fg: '#e65100' }, // A orange
  '6': { bg: '#e8f5e9', border: '#a5d6a7', fg: '#2e7d32' }, // S green
};
const DEFAULT_COLOR = { bg: '#eeeeee', border: '#bdbdbd', fg: '#616161' };

// 額面金額の表示（1億以上は億、未満は万）
const fmtAmount = (v?: number | null): string => {
  if (!v) return '-';
  if (Math.abs(v) >= 1e8) return `${(v / 1e8).toFixed(1)}億`;
  return `${Math.round(v / 1e4).toLocaleString()}万`;
};

// フェーズ名から「2.」等の先頭番号を除いた短縮名
const phaseShortName = (phase: string): string => phase.replace(/^\d+\./, '');

interface ColumnData {
  ref: FunnelColumnRef;
  phase: string;
  rankLabel: string;
  subLabel: string;
  probability: number;
  color: { bg: string; border: string; fg: string };
  count: number;
  amount: number;
  weighted: number;
  delta: number | null;   // 前断面比（前断面なしは null）
  lostCount: number;
  lostAmount: number;
  isBacklog: boolean;
}

export default function PipelineFunnelBoard({ snapshotDate, summary, lostRows, phases, onColumnClick }: Props) {
  const { columns, pipelineTotal, lostTotal, hasPrev } = useMemo(() => {
    const latest = summary.filter((r) => r.snapshot_date === snapshotDate);
    const prevDate = Array.from(new Set(summary.map((r) => r.snapshot_date)))
      .filter((d) => d < snapshotDate)
      .sort()
      .pop();
    const prev = prevDate ? summary.filter((r) => r.snapshot_date === prevDate) : [];
    const cellOf = (rows: PipelineSummaryRow[], key: string) =>
      rows.find((r) => (r.rank_code || r.aggregate_class) === key);

    // 失注集計を「変更前フェーズ」単位に集約
    const lostByPhase = new Map<string, { count: number; amount: number }>();
    let lostCountTotal = 0;
    let lostAmountTotal = 0;
    lostRows.forEach((r) => {
      lostCountTotal += r.lost_count;
      lostAmountTotal += r.lost_amount_sum;
      if (!r.phase_before) return;
      const e = lostByPhase.get(r.phase_before) || { count: 0, amount: 0 };
      e.count += r.lost_count;
      e.amount += r.lost_amount_sum;
      lostByPhase.set(r.phase_before, e);
    });

    const cols: ColumnData[] = phases
      .filter((p) => ['prospect', 'pipeline', 'backlog'].includes(p.aggregate_class))
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((p) => {
        const key = p.rank_code || p.aggregate_class;
        const cell = cellOf(latest, key);
        const prevCell = cellOf(prev, key);
        const lost = lostByPhase.get(p.phase);
        return {
          ref: {
            key,
            label: p.rank_code ? `${p.rank_code}ランク` : '見込み（ランク前）',
            phase: p.phase,
            aggregateClass: p.aggregate_class,
            rankCode: p.rank_code || undefined,
          },
          phase: p.phase,
          rankLabel: p.rank_code || '見込み',
          subLabel: phaseShortName(p.phase),
          probability: p.probability,
          color: FUNNEL_COLORS[p.phase.charAt(0)] || DEFAULT_COLOR,
          count: cell?.project_count ?? 0,
          amount: cell?.amount_sum ?? 0,
          weighted: cell?.weighted_sum ?? 0,
          delta: prevDate ? (cell?.amount_sum ?? 0) - (prevCell?.amount_sum ?? 0) : null,
          lostCount: lost?.count ?? 0,
          lostAmount: lost?.amount ?? 0,
          isBacklog: p.aggregate_class === 'backlog',
        };
      });

    const pipelineCols = cols.filter((c) => c.ref.aggregateClass === 'pipeline');
    return {
      columns: cols,
      pipelineTotal: {
        count: pipelineCols.reduce((s, c) => s + c.count, 0),
        amount: pipelineCols.reduce((s, c) => s + c.amount, 0),
        weighted: pipelineCols.reduce((s, c) => s + c.weighted, 0),
      },
      lostTotal: { count: lostCountTotal, amount: lostAmountTotal },
      hasPrev: !!prevDate,
    };
  }, [summary, lostRows, phases, snapshotDate]);

  // 柱の高さ = 件数の√スケール（最大値基準で 40〜220px）
  const maxSqrt = Math.max(1, ...columns.map((c) => Math.sqrt(c.count)));
  const barHeight = (count: number) =>
    count > 0 ? Math.max(44, Math.round((Math.sqrt(count) / maxSqrt) * 220)) : 44;

  const funnelCols = columns.filter((c) => !c.isBacklog);
  const backlogCol = columns.find((c) => c.isBacklog);

  const renderDelta = (delta: number | null) => {
    if (delta === null || Math.abs(delta) < 1e4) return null;
    const up = delta > 0;
    return (
      <Box
        sx={{
          position: 'absolute', top: -22, fontSize: '0.6875rem', fontWeight: 700,
          borderRadius: '10px', px: 1, whiteSpace: 'nowrap',
          color: up ? '#2e7d32' : '#c62828',
          bgcolor: up ? '#e8f5e9' : '#ffebee',
        }}
      >
        {up ? '▲+' : '▼−'}{fmtAmount(Math.abs(delta))}
      </Box>
    );
  };

  return (
    <Paper sx={{ borderRadius: 2, mb: 2, p: 2, overflow: 'hidden' }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
          案件推進ボード
        </Typography>
        <Chip
          label={`断面 ${snapshotDate.replace(/-/g, '/')}`}
          size="small"
          sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#eceff1', color: '#455a64', fontWeight: 600 }}
        />
        <Typography variant="caption" color="text.secondary">
          柱の高さ=件数 ・ 金額=額面残計 ・ 柱クリックで明細{!hasPrev && '（増減バッジは次回断面から表示）'}
        </Typography>
      </Box>

      {/* KPI タイル */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2, px: 2, py: 1, minWidth: 180, bgcolor: '#fbfcfd' }}>
          <Typography variant="caption" color="text.secondary">推進残 計（D〜A）</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.3 }}>{fmtAmount(pipelineTotal.amount)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {pipelineTotal.count}件 ・ 加重 {fmtAmount(pipelineTotal.weighted)}
          </Typography>
        </Box>
        <Box sx={{ border: '1px solid #c8e6c9', borderRadius: 2, px: 2, py: 1, minWidth: 180, bgcolor: '#e8f5e9' }}>
          <Typography variant="caption" color="text.secondary">受注残（Sランク）</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.3, color: '#2e7d32' }}>{fmtAmount(backlogCol?.amount)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {backlogCol?.count ?? 0}件 ・ 金額/納期 確定
          </Typography>
        </Box>
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2, px: 2, py: 1, minWidth: 180, bgcolor: '#fbfcfd' }}>
          <Typography variant="caption" color="text.secondary">期間内 失注</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.3, color: lostTotal.count > 0 ? '#c62828' : 'text.primary' }}>
            {lostTotal.count}件
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lostTotal.count > 0 ? `額面 ${fmtAmount(lostTotal.amount)}` : '2026-08-21以降の変更から蓄積'}
          </Typography>
        </Box>
      </Box>

      {/* ファネル本体（横スクロール許容） */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 760 }}>
          {/* 軸ラベル */}
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'grey.500', fontSize: '0.75rem', px: 0.5, pb: 0.75 }}>
            <span>活動幅（母集団の広さ）</span>
            <Box sx={{
              flex: 1, borderTop: '1px dashed #cfd8dc', mx: 1.5, position: 'relative',
              '&::after': {
                content: '""', position: 'absolute', right: -1, top: -4.5,
                border: '4px solid transparent', borderLeftColor: '#cfd8dc',
              },
            }} />
            <span>活動の質（成約への接近）</span>
          </Box>

          {/* 柱 */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.75 }}>
            {funnelCols.map((c) => (
              <Box key={c.ref.key} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                  <Typography component="div" sx={{ fontSize: '1.25rem', fontWeight: 800, color: c.color.fg }}>
                    {c.rankLabel}
                  </Typography>
                  <Typography component="div" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'grey.500', whiteSpace: 'nowrap' }}>
                    {c.subLabel}
                  </Typography>
                </Box>
                <Box
                  onClick={() => onColumnClick(c.ref)}
                  sx={{
                    width: '100%', height: barHeight(c.count), position: 'relative', cursor: 'pointer',
                    bgcolor: c.color.bg, border: '1.5px solid', borderColor: c.color.border, borderBottom: 'none',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1,
                    transition: 'filter .15s', '&:hover': { filter: 'brightness(0.96)' },
                  }}
                >
                  {renderDelta(c.delta)}
                  <Typography sx={{ fontSize: '1.0625rem', fontWeight: 800, lineHeight: 1.2 }}>
                    {fmtAmount(c.amount)}
                  </Typography>
                  {barHeight(c.count) >= 64 && (
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {c.count}件{c.weighted ? ` ・ 加重 ${fmtAmount(c.weighted)}` : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            {/* 成約ライン */}
            <Box sx={{
              width: 2, alignSelf: 'stretch', position: 'relative',
              background: 'repeating-linear-gradient(#b0bec5 0 6px, transparent 6px 12px)',
            }}>
              <Box sx={{
                position: 'absolute', top: -4, left: '50%', transform: 'translateX(-50%)',
                bgcolor: 'grey.600', color: 'white', fontSize: '0.65rem', borderRadius: '8px',
                px: 1, whiteSpace: 'nowrap', letterSpacing: '0.08em',
              }}>
                成約ライン
              </Box>
            </Box>

            {/* S 受注残 */}
            {backlogCol && (
              <Box sx={{ flex: 1.15, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                  <Typography component="div" sx={{ fontSize: '1.25rem', fontWeight: 800, color: backlogCol.color.fg }}>
                    {backlogCol.rankLabel}
                  </Typography>
                  <Typography component="div" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'grey.500', whiteSpace: 'nowrap' }}>
                    {backlogCol.subLabel}
                  </Typography>
                </Box>
                <Box
                  onClick={() => onColumnClick(backlogCol.ref)}
                  sx={{
                    width: '100%', height: Math.min(232, barHeight(backlogCol.count) + 12), position: 'relative', cursor: 'pointer',
                    bgcolor: backlogCol.color.bg, border: '1.5px solid', borderColor: backlogCol.color.border, borderBottom: 'none',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1,
                    transition: 'filter .15s', '&:hover': { filter: 'brightness(0.96)' },
                  }}
                >
                  {renderDelta(backlogCol.delta)}
                  <Typography sx={{ fontSize: '1.125rem', fontWeight: 800, color: backlogCol.color.fg, lineHeight: 1.2 }}>
                    {fmtAmount(backlogCol.amount)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                    {backlogCol.count}件
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.625rem', color: backlogCol.color.fg, bgcolor: 'white',
                    border: '1px solid #a5d6a7', borderRadius: '8px', px: 1, mt: 0.5, whiteSpace: 'nowrap',
                  }}>
                    受注確定日ベースで記録
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* ベースライン */}
          <Box sx={{ borderTop: '2px solid #b0bec5' }} />

          {/* 失注ゾーン + 確度 */}
          <Box sx={{ display: 'flex', gap: 1.75, mt: 0.25 }}>
            {funnelCols.map((c) => (
              <Box key={c.ref.key} sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                {c.ref.aggregateClass === 'pipeline' && (
                  <Box sx={{
                    mx: 'auto', width: '72%', bgcolor: '#ffebee', color: '#c62828',
                    borderRadius: '0 0 8px 8px', fontSize: '0.6875rem', fontWeight: 600, py: 0.25,
                  }}>
                    失注 {c.lostCount}件{c.lostCount > 0 ? `・${fmtAmount(c.lostAmount)}` : ''}
                  </Box>
                )}
                <Typography sx={{ fontSize: '0.6875rem', color: 'grey.500', mt: 0.5 }}>
                  確度 {c.probability}%
                </Typography>
              </Box>
            ))}
            <Box sx={{ width: 2 }} />
            {backlogCol && (
              <Box sx={{ flex: 1.15, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.6875rem', color: backlogCol.color.fg, mt: 0.5, fontWeight: 600 }}>
                  確定（確度100%）
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
